import { IntlMessageFormat } from 'intl-messageformat';
import { DEFAULT_LOCALE } from './resolve';

/**
 * A string that is copy but has nowhere to be translated yet.
 *
 * Importable on its own as `@foir/i18n/message`. It pulls in no React and no
 * i18next, so a data package like `@foir/access-vocab` can declare its prose as
 * {@link Message} pairs without dragging a UI runtime into the Node service
 * images that consume it.
 *
 * # The problem this exists for
 *
 * A large share of this console's copy lives in const tables evaluated when the
 * module loads: nav items, page titles, widget config, expression-editor help,
 * the empty-state blocks. `useT()` is a hook, so it cannot run there, and a
 * non-hook `t()` reading a module singleton would run BEFORE i18next has
 * initialised and bake English into the table for the life of the process. That
 * failure mode is the worst kind: it looks like it works.
 *
 * So the table stores the KEY and the English together, and the component that
 * renders it resolves the pair at render, where a hook is legal:
 *
 *     // module scope
 *     const NAV = [{ name: msg('nav.home', 'Home'), href: '/' }];
 *
 *     // in a component
 *     const t = useT();
 *     return <span>{t(item.name)}</span>;
 *
 * # Why it is an object and not two fields
 *
 * The type changes from `string` to `Message`, which is the point. Every place
 * that renders one becomes a type error until it passes through `t`, so the
 * migration is driven by the compiler rather than by grep. A `Message` reaching
 * JSX unresolved is caught too: React will not render an arbitrary object, and
 * `tsc` says so before it ever runs.
 */

export interface Message {
  readonly key: string;
  readonly defaultValue: string;
}

/** Pair a catalogue key with the English behind it, for resolution at render. */
export function msg(key: string, defaultValue: string): Message {
  return { key, defaultValue };
}

/**
 * Either a {@link Message} to resolve, or a string that is already final.
 *
 * Some tables hold both. `ContextVariable.label` in the rules editor is a
 * hand-written label for a system context ("Current time") on one row and the
 * NAME OF A FIELD THE USER DEFINED on the next. The first is copy; the
 * second is their data and translating it would be a bug, not a feature.
 *
 * Widening such a field to `Message` outright was wrong for exactly that reason.
 * This says what is actually true: some of these are ours to translate and some
 * are not, and the value itself carries which.
 */
export type Translatable = string | Message;

/**
 * What a placeholder in a message is filled with: a value, already final.
 *
 * Not a {@link Message}. That is copy still to be resolved, and handed over as
 * a parameter it reached the screen as an object: the formatter answers an
 * object parameter with a list of parts and React will not render the object
 * in it. The password strength line crashed every password page that way.
 * Resolve it first, `t(message)`, and pass the string.
 */
export type MessageParam = string | number | boolean | bigint | Date | null | undefined;

/** A message's parameters, by placeholder name. */
export type MessageParams = Readonly<Record<string, MessageParam>>;

export function isMessage(value: unknown): value is Message {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as Message).key === 'string' &&
    typeof (value as Message).defaultValue === 'string'
  );
}

/**
 * A translator that always renders the in-code English.
 *
 * For surfaces deliberately outside the translation scope. `apps/staff` is
 * Fóir-internal and is not extracted, but it still consumes shared components
 * whose copy is declared as {@link Message} pairs, and those have to resolve to
 * something. Naming this explicitly beats mounting an i18n provider on a console
 * nobody wants translated, and it beats a `useT()` whose no-op behaviour is
 * incidental rather than stated.
 */
/**
 * What resolves copy at render.
 *
 * Declared here rather than beside the provider because it is a pure type, and
 * a package with no React — the transport's error helper, a data package — has
 * to be able to take one as a parameter without importing a UI runtime.
 */
export interface Translator {
  (key: string, defaultValue: string, params?: MessageParams): string;
  (message: Message, params?: MessageParams): string;
  /**
   * Resolve a value that may or may not be ours to translate. A `Message` goes
   * through the catalogue; a plain string is returned untouched, because it is
   * already final text such as a user's own field name.
   */
  (value: Translatable): string;
}

export const englishTranslator = ((
  keyOrMessage: Translatable,
  ...rest: Array<unknown>
): string => {
  // A bare string with no default is already-final text, not a key. Returning
  // the missing second argument here handed callers `undefined`, which is how
  // `@foir/access`'s vocabulary test found this.
  if (typeof keyOrMessage === 'string') {
    // `(value)` is finished text; `(key, defaultValue, params?)` is a call site
    // written out in full, whose THIRD argument holds the parameters.
    return rest.length === 0
      ? keyOrMessage
      : formatDefault(rest[0] as string, rest[1] as Record<string, unknown>);
  }
  return formatDefault(
    keyOrMessage.defaultValue,
    rest[0] as Record<string, unknown>,
  );
}) as {
  (key: string, defaultValue: string, params?: MessageParams): string;
  (message: Message, params?: MessageParams): string;
  (value: Translatable): string;
};

/**
 * Render an in-code default the way the runtime would.
 *
 * A default carrying ICU — a plural block, a named parameter — is a MESSAGE, not
 * a finished sentence, and handing it back untouched puts `{count, plural, …}`
 * on the screen. Shared with the provider's own no-catalogue path so both
 * fallbacks read identically.
 */
export function formatDefault(
  defaultValue: string,
  params?: Record<string, unknown>,
): string {
  if (!defaultValue.includes('{')) return defaultValue;
  try {
    return new IntlMessageFormat(defaultValue, DEFAULT_LOCALE).format(
      params ?? {},
    ) as string;
  } catch {
    // A malformed message must not take the screen down. The un-formatted
    // English still says most of what it meant.
    return defaultValue;
  }
}
